/**
 * CW-958: footer course topic → sessionStorage → /courses applies interest then clears storage.
 * Pure helpers keep URL vs storage precedence testable without Nuxt/Pinia.
 */

export const FOOTER_COURSE_TOPIC_STORAGE_KEY = 'footer-course-topic-interest'

export type ResolveInitialInterestsSelectionParams = {
  queryInterests: unknown
  interestFilterDisplay: boolean
  extractGenres: (queryInterests: unknown) => string[]
  footerTopicFromStorage: string | null
  storeInterestType: string
}

/**
 * Mirrors `pages/courses/index.vue` initial `interestsSelected` derivation.
 */
export function resolveInitialInterestsSelection(
  params: ResolveInitialInterestsSelectionParams,
): string[] {
  const {
    queryInterests,
    interestFilterDisplay,
    extractGenres,
    footerTopicFromStorage,
    storeInterestType,
  } = params
  if (queryInterests && interestFilterDisplay) {
    return extractGenres(queryInterests)
  }
  return [footerTopicFromStorage || storeInterestType]
}

export type ResolveFooterTopicMountEffectsParams = {
  /** Raw `sessionStorage.getItem` result (null if missing). */
  storedTopic: string | null
  /** True when `route.query.interests` is set (URL wins; storage still cleared on mount). */
  hasQueryInterests: boolean
}

/**
 * Mirrors `onMounted` footer-topic block: optionally sync from storage; always clear when a value was stored.
 */
export function resolveFooterTopicMountEffects(
  params: ResolveFooterTopicMountEffectsParams,
): { shouldApplyFromStorage: boolean; shouldClearStorage: boolean } {
  if (!params.storedTopic) {
    return { shouldApplyFromStorage: false, shouldClearStorage: false }
  }
  return {
    shouldApplyFromStorage: !params.hasQueryInterests,
    shouldClearStorage: true,
  }
}

/**
 * Persists genre for hard/soft navigation handoff to `/courses` (client-only caller).
 */
export function persistFooterCourseTopicInterest(
  storage: Pick<Storage, 'setItem'>,
  genre: unknown,
): void {
  if (typeof genre !== 'string' || !genre) return
  storage.setItem(FOOTER_COURSE_TOPIC_STORAGE_KEY, genre)
}

export type ApplyFooterCourseTopicHandoffParams = {
  storedTopic: string | null
  hasQueryInterests: boolean
  storage: Pick<Storage, 'removeItem'>
  onApplyFromStorage: (topic: string) => void
}

/**
 * Applies footer handoff from sessionStorage and clears the one-shot key.
 * Safe to call on every `/courses` navigation (including SPA revisits where `onMounted` does not re-run).
 */
export function applyFooterCourseTopicHandoff(
  params: ApplyFooterCourseTopicHandoffParams,
): boolean {
  const { storedTopic, hasQueryInterests, storage, onApplyFromStorage } = params
  if (!storedTopic) return false

  const mountEffects = resolveFooterTopicMountEffects({
    storedTopic,
    hasQueryInterests,
  })

  if (mountEffects.shouldApplyFromStorage) {
    onApplyFromStorage(storedTopic)
  }
  if (mountEffects.shouldClearStorage) {
    storage.removeItem(FOOTER_COURSE_TOPIC_STORAGE_KEY)
  }

  return true
}
